import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Button, Flex, View, Checkbox, Link, IconPlayLine, IconPauseLine, IconResetLine, IconMoveStartLine } from '@smarterservices/smarter-ui';
import { WebcamNotFoundError } from '../../helpers/definedErrors';
import DualPane from '../DualPane';
import StepSuccessMessage from '../Common/StepSuccessMessage';
import StepInstructions from '../Common/StepInstructions';
import { usePaneViewer } from '../PaneViewer';
import { useAppStore } from '../../stores/app';

const RoomScan = ({ stepData, setIsTaskComplete, setStepMetadata }) => {
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [roomScanStarted, setRoomScanStarted] = useState(false);
  const [roomScanCompleted, setRoomScanCompleted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const demoVideoRef = useRef(null);
  const [showRoomScan, setShowRoomScan] = useState(false);
  const [videoState, setVideoState] = useState('unplayed');
  const { onNext } = usePaneViewer();
  const { apiClient } = useAppStore();
  const isMandatory = stepData?.roomScanVideoMandatory || false;

  useEffect(() => {
    handleSelectedVideoDevice();
  }, []);

  const handleSelectedVideoDevice = async () => {
    try {
      // Get the video device selected from ShareWebcam
      const selectedDeviceLabel = localStorage.getItem('spSelectedWebcamLabel');
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      const selectedCamera = cameras.find(
        (camera) => camera.label === selectedDeviceLabel
      );

      if (selectedCamera) {
        const userStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: selectedCamera.deviceId },
          },
          audio: false,
        });
        setStream(userStream);
      } else {
        throw new WebcamNotFoundError();
      }
    } catch (error) {
      console.log('Error fetching video devices:', error);
    }
  };

  const stopRoomScan = async () => {

    if (roomScanStarted) {
      // End Room Scan
      setRoomScanStarted(false);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setShowSuccess(true);
      setRoomScanCompleted(true);
      setIsTaskComplete(true);
      
      try {

        await apiClient.addAnnotation({
          payload: {
            typeId: 'room-scan-stop',
          }
        });

      } catch (error) {
        //TODO: What do we want to do here?
        console.error('Failed to create stop annotation:', error);
      }
    }     
  };

  const startRoomScan = async () => {
    // Start Room Scan
    setRoomScanStarted(true);
    setShowSuccess(false);
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
    }
    setIsTaskComplete(false);
    
    try {
      await apiClient.addAnnotation({
        payload: {
          typeId: 'room-scan-start',
        }
      });
    } catch (error) {
      //TODO: What do we want to do here?
      console.error('Failed to create start annotation:', error);
    }
  };

  const getButtonLabel = () => {
    if (roomScanStarted) {
      return stepData?.buttonAfter;
    }
    if (roomScanCompleted) {
      return stepData?.buttonRetake;
    }
    return stepData?.buttonBefore;
  };

  const videoStyles = {
    width: '100%',
    marginTop: '20px',
    ...(roomScanStarted && {
      animation: 'pulse 2s infinite',
      border: '3px solid #ff0000',
    }),
  };

  const pulseKeyframes = `
    @keyframes pulse {
      0% {
        border-color: rgba(255, 0, 0, 0.5);
      }
      50% {
        border-color: rgba(255, 0, 0, 1);
      }
      100% {
        border-color: rgba(255, 0, 0, 0.5);
      }
    }
  `;

  const handleVideoEnd = () => {
    setVideoCompleted(true);
    setVideoState('watched');
  };

  const handleAcknowledge = (e) => {
    setAcknowledged(e.target.checked);
  };

  const handleAcknowledgeComplete = () => {
    setShowRoomScan(true);
    // we need this setTimeout to allow the state to update before starting the room scan.
    setTimeout(() => {
      startRoomScan();
    }, 100);
  };

  const handleContinue = () => {
    // Store some of the state data just for info purposes...
    setStepMetadata({
      isVideoMandatory: isMandatory,
      videoStarted,
      videoCompleted,
    });
    setIsTaskComplete(true);
    onNext();
  };

  const handleVideoPlay = () => {
    if (demoVideoRef.current) {
      demoVideoRef.current.play();
      setVideoState('playing');
      setVideoStarted(true);
    }
  };

  const handleVideoPause = () => {
    if (demoVideoRef.current) {
      demoVideoRef.current.pause();
      setVideoState('paused');
    }
  };

  const handleVideoRewind = () => {
    if (demoVideoRef.current) {
      demoVideoRef.current.currentTime = Math.max(
        0,
        demoVideoRef.current.currentTime - 10
      );
      setVideoState('playing');
    }
  };

  const RenderVideoControls = useMemo(() => {
    const playButton = <Button
                          color="primary"
                          onClick={
                            videoCompleted
                              ? () => {
                                  demoVideoRef.current.currentTime = 0;
                                  handleVideoPlay();
                                }
                              : handleVideoPlay
                          }
                          margin="0 small"
                        >
                          <IconPlayLine /> {stepData?.videoControls?.playButton}
                        </Button>;
    const replayButton = <Button
                      color="primary"
                      onClick={
                        videoCompleted
                          ? () => {
                              demoVideoRef.current.currentTime = 0;
                              handleVideoPlay();
                            }
                          : handleVideoPlay
                      }
                      margin="0 small"
                      >
                        <IconMoveStartLine /> {stepData?.videoControls?.replayButton}
                      </Button>;
    const pauseButton = <Button
                      color="primary"
                      onClick={handleVideoPause}
                      margin="0 small"
                      >
                       <IconPauseLine /> {stepData?.videoControls?.pauseButton}
                      </Button>;

    const jumpBackButton = <Button
                          color="primary"
                          onClick={handleVideoRewind}
                          margin="0 small"
                          >
                          <IconResetLine /> {stepData?.videoControls?.jumpBackButton}
                          </Button>;

    if(videoState === 'unplayed'){
      return (
        <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
          {playButton}
        </Flex>
      );
    } else if (videoState === 'playing') {
      return (
        <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
          {pauseButton}
          {jumpBackButton}
        </Flex>
      );
    } else if (videoState === 'paused') {
      return (
        <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
          {playButton}
          {jumpBackButton}
        </Flex>
      );
    } else if (videoState === 'watched') {
      return (
        <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
          {replayButton}
        </Flex>
      );
    }
    return null;
  }, [videoState, videoCompleted, stepData?.videoControls, handleVideoPlay, handleVideoPause, handleVideoRewind]);

  const renderRoomScanControlButtons = useMemo(() => {
    if(roomScanStarted){
      return (
          <Button
            color="primary"
            size="medium"
            onClick={stopRoomScan}
            margin="small 0"
          >
            {stepData?.buttonAfter}
          </Button>
      );
   
    } else if(roomScanCompleted){
      return (
        <>
          <Button
            color="primary"
            size="medium"
            onClick={handleContinue}
            margin="medium 0"
          >
            {stepData?.nextStepButton}
          </Button>
          <Link
            as="button"
            onClick={startRoomScan}
            isWithinText={false}
            margin="small 0"
          >
            {stepData?.buttonRetake}
          </Link>
        </>
      );
    } else {
      return (
        <Button
          color="primary"
          size="medium"
          onClick={startRoomScan}
          margin="small 0"
        >
          {stepData?.buttonBefore}
        </Button>
      );
    }
  }, [roomScanStarted, roomScanCompleted, stepData, stopRoomScan, startRoomScan, handleContinue]);

  return (
    <DualPane
      leftPane={
        <StepInstructions instructions={stepData?.body} />
      }
      rightPane={
        <Flex direction="column" alignItems="center" width="100%">
          <style>{pulseKeyframes}</style>
          <div
            style={{
              minHeight: '400px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {!showRoomScan ? (
              <Flex direction="column" alignItems="center" width="100%">
                <View as="div">
                  <video
                    ref={demoVideoRef}
                    src={stepData?.roomScanVideoUrl}
                    style={{
                      width: '100%',
                      maxHeight: '40vh',
                      objectFit: 'contain',                      
                      borderRadius: '4px',
                      border: `1px solid #ccc`,
                      margin: '0',
                      boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0.1875rem 0.375rem, rgba(0, 0, 0, 0.16) 0px 0.1875rem 0.375rem',
                    }}
                    onEnded={handleVideoEnd}
                    controls={false}
                  />
                </View>
                {RenderVideoControls}
                <Flex direction="column" alignItems="center" margin="medium 0">
                  {((isMandatory && videoCompleted) || !isMandatory) && (
                    <>
                      <Flex
                        direction="row"
                        alignItems="center"
                        margin="0 0 medium 0"
                      >
                        <Checkbox
                          id="acknowledge"
                          label={stepData?.acknowledgementText}
                          checked={acknowledged}
                          onChange={handleAcknowledge}
                        />
                      </Flex>
                      <Flex direction="column" alignItems="center" height="">
                      {acknowledged && (
                        <Button
                          color="primary"
                          size="medium"
                          margin="0 0 medium 0"
                          onClick={handleAcknowledgeComplete}
                        >
                          {stepData?.videoControls?.continueButton}
                        </Button>
                      )}
                      </Flex>
                    </>
                  )}
                </Flex>
              </Flex>
            ) : (
              <Flex direction="column" alignItems="center" width="100%">
                <div style={{ position: 'relative' }}>
                  <video
                    ref={videoRef}
                    style={videoStyles}
                    autoPlay
                    playsInline
                  />
                  {roomScanStarted && (
                    <>
                      <div
                        style={{
                          position: 'absolute',
                          top: '30px',
                          right: '10px',
                          width: '20px',
                          height: '20px',
                          backgroundColor: '#ff0000',
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: '25px',
                          right: '35px',
                          color: '#ffffff',
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          padding: '5px 10px',
                          borderRadius: '4px',
                        }}
                      >
                        {stepData?.recordingText}
                      </div>
                    </>
                  )}
                </div>
                {showSuccess && (
                  <StepSuccessMessage message={stepData.successMessage} />
                )}
                {renderRoomScanControlButtons}
              </Flex>
            )}
          </div>
        </Flex>
      }
    />
  );
};

export default RoomScan;
